<template>
    <div>
        <div
            v-if="isCreatable"
            class="kgr-region kgr-region--lightGray kgr-region--rounded kgr-row kgr-row--bigMargin"
        >
            <h2
                v-t="'member.relation.create.title'"
                class="kgr-headline kgr-headline--h3 kgr-headline--text kgr-headline--firstInText"
            ></h2>
            <div class="kgr-postfix kgr-postfix--gutters kgr-postfix--top">
                <div class="kgr-postfix__fixed">
                    {{ $t('member.relation.create.text', { fullName }) }}
                </div>

                <div
                    class="kgr-postfix__fixed"
                    :class="{ 'error-state': error.forField('relationType') }"
                >
                    <kgr-relation-select
                        v-model="relationType"
                        :gender="gender"
                    />
                </div>

                <div
                    class="kgr-postfix__fixed"
                    :class="{ 'error-state': error.forField('relationHuman') }"
                >
                    <kgr-club-member-select
                        v-model="relationHuman"
                        :club-id="clubId"
                        @update:members="filterAndSortMembers"
                    />
                </div>

                <div class="kgr-postfix__fixed">
                    <kgr-button
                        :value="$t('add')"
                        :pending="pendingCreate"
                        :disabled="disabled"
                        @click="addRelation()"
                    />
                </div>
            </div>
        </div>

        <div v-if="emptyRelations">
            <i v-t="'Es sind keine Beziehungen vorhanden.'"></i>
        </div>

        <div v-else>
            <kgr-member-relation-list
                v-if="member"
                :title="$t('Kinder')"
                :pending-remove="pendingRemove"
                :relations="childRelations"
                :legal-representatives="legalRepresentatives"
                class="kgr-row"
                :is-deletable="isDeletable"
                :legal-member="member"
                @remove="$emit('removeRelation', $event)"
                @addLegalRepresentative="$emit('addLegalRepresentative', $event)"
            />

            <kgr-member-relation-list
                v-if="member"
                :title="$t('Eltern')"
                :pending-remove="pendingRemove"
                :relations="parentRelations"
                :legal-representatives="legalRepresentatives"
                class="kgr-row"
                :is-deletable="isDeletable"
                :legal-member="member"
                @remove="$emit('removeRelation', $event)"
                @addLegalRepresentative="$emit('addLegalRepresentative', $event)"
            />

            <kgr-member-relation-list
                v-if="member"
                :title="$t('Geschwister')"
                :pending-remove="pendingRemove"
                :relations="siblingRelations"
                :legal-representatives="legalRepresentatives"
                class="kgr-row"
                :is-deletable="isDeletable"
                :legal-member="member"
                @remove="$emit('removeRelation', $event)"
                @addLegalRepresentative="$emit('addLegalRepresentative', $event)"
            />

            <kgr-member-relation-list
                v-if="member"
                :title="$t('Ehepartner')"
                :pending-remove="pendingRemove"
                :relations="spouseRelations"
                :legal-representatives="legalRepresentatives"
                class="kgr-row"
                :is-deletable="isDeletable"
                :legal-member="member"
                @remove="$emit('removeRelation', $event)"
                @addLegalRepresentative="$emit('addLegalRepresentative', $event)"
            />

            <kgr-member-relation-list
                v-if="member"
                :title="$t('Familienmitglieder')"
                :pending-remove="pendingRemove"
                :relations="familyRelations"
                :legal-representatives="legalRepresentatives"
                :is-deletable="isDeletable"
                :legal-member="member"
                @remove="$emit('removeRelation', $event)"
                @addLegalRepresentative="$emit('addLegalRepresentative', $event)"
            />
        </div>
    </div>
</template>

<script>
import ClubMemberSelect from '../common/form/select/ClubMemberSelect';
import Error from '../../js/model/Error';
import KgrButton from '../common/form/button/KgrButton';
import KgrMemberRelationList from './MemberRelationList';
import MemberRepository from '../../js/repository/MemberRepository';
import RelationSelect from '../common/form/select/RelationSelect';

export default {
    components: {
        KgrMemberRelationList,
        'kgr-button': KgrButton,
        'kgr-relation-select': RelationSelect,
        'kgr-club-member-select': ClubMemberSelect,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        memberId: {
            type: Number,
            required: true,
        },
        relations: {
            type: Array,
            required: true,
        },
        legalRepresentatives: {
            type: Array,
            required: true,
        },
        pendingCreate: {
            type: Boolean,
            required: false,
            default: false,
        },
        pendingRemove: {
            type: Boolean,
            required: false,
            default: false,
        },
        isCreatable: {
            type: Boolean,
            required: true,
        },
        isDeletable: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            error: new Error(),
            member: null,
            relationType: null,
            relationHuman: null,
        };
    },
    computed: {
        fullName() {
            if (!this.member) {
                return '';
            }

            return this.member.fullName();
        },
        gender() {
            if (!this.member) {
                return '';
            }

            return this.member.gender;
        },
        disabled() {
            return this.relationType === null || this.relationHuman === null;
        },
        emptyRelations() {
            return this.parentRelations.length === 0
                && this.childRelations.length === 0
                && this.siblingRelations.length === 0
                && this.spouseRelations.length === 0
                && this.familyRelations.length === 0;
        },
        parentRelations() {
            return this.relations
                .filter((relation) => relation.type === 'parent_child')
                .filter((relation) => relation.memberB.id === this.memberId)
                .map((relation) => {
                    return { id: relation.id, member: relation.memberA };
                });
        },
        childRelations() {
            return this.relations
                .filter((relation) => relation.type === 'parent_child')
                .filter((relation) => relation.memberA.id === this.memberId)
                .map((relation) => {
                    return { id: relation.id, member: relation.memberB };
                });
        },
        siblingRelations() {
            return this.relations
                .filter((relation) => relation.type === 'sibling')
                .map((relation) => {
                    const member = relation.memberA.id === this.memberId ? relation.memberB : relation.memberA;

                    return { id: relation.id, member };
                });
        },
        spouseRelations() {
            return this.relations
                .filter((relation) => relation.type === 'spouse')
                .map((relation) => {
                    const member = relation.memberA.id === this.memberId ? relation.memberB : relation.memberA;

                    return { id: relation.id, member };
                });
        },
        familyRelations() {
            return this.relations
                .filter((relation) => relation.type === 'family')
                .map((relation) => {
                    const member = relation.memberA.id === this.memberId ? relation.memberB : relation.memberA;

                    return { id: relation.id, member };
                });
        },
    },
    mounted() {
        MemberRepository.find(this.memberId)
            .then((member) => this.member = member)
            .catch(() => this.$notify.error('error.loadingData'));
    },
    methods: {
        filterAndSortMembers(props) {
            const relatedHumans = [];
            this.siblingRelations.forEach((relation) => relatedHumans.push(relation.member));
            this.spouseRelations.forEach((relation) => relatedHumans.push(relation.member));
            this.parentRelations.forEach((relation) => relatedHumans.push(relation.member));
            this.childRelations.forEach((relation) => relatedHumans.push(relation.member));
            this.familyRelations.forEach((relation) => relatedHumans.push(relation.member));

            const members = props.members.filter((member) => {
                return this.memberId !== member.id && relatedHumans.filter((relatedHuman) => {
                    return relatedHuman.id === member.id;
                }).length === 0;
            });

            const compare = (o1, o2) => {
                const s1 = o1.firstName + o1.lastName;
                const s2 = o2.firstName + o2.lastName;

                return s1.toLowerCase().localeCompare(s2.toLowerCase());
            };

            props.members = members.sort(compare);
        },
        addRelation() {
            this.error = new Error();

            if (this.relationType === null) {
                this.error.add('relationType', 'required!');
            }
            if (this.relationHuman === null) {
                this.error.add('relationHuman', 'required!');
            }

            if (!this.error.isEmpty()) {
                return false;
            }

            this.$emit(`add${this.capitalize(this.relationType)}`, this.relationHuman);
        },
        capitalize(string) {
            return string.charAt(0).toUpperCase() + string.slice(1);
        },
    },
};
</script>

<style scoped lang="scss" rel="stylesheet/scss">
  .error-state {
    border-color: #f44336;
}
</style>
